// A deadline on every Supabase AUTH request.
//
// The disease (founder, 08-04: "cmd r does this, and this is bull shit"): the
// app can freeze on the branded gate curtain forever. `AuthContext.loading`
// starts true and only clears from an `onAuthStateChange` verdict — and both
// that listener and the `getSession()` "eager fallback" open with
// `await initializePromise`, so the documented safety net cannot resolve one
// millisecond before the thing it backs up. That promise awaits a token
// refresh whose fetch carries NO AbortSignal (auth-js `_refreshAccessToken`).
// A network that FAILS resolves inside the ~30s retry budget; a half-open
// socket — a Mac waking with the interface up but routing dead — never
// resolves at all. Nothing downstream can recover, because nothing downstream
// ever runs.
//
// Bounding the request converts that hang into an outcome the existing state
// machine already handles: auth-js turns any fetch throw into an
// AuthRetryableFetchError, `_recoverAndRefresh` preserves the session for
// retryable errors (only NON-retryable ones destroy it), so the abort costs
// nothing on a flaky network — it retries on its own backoff, then resolves,
// then INITIAL_SESSION fires and the app renders.
//
// Scoped to /auth/v1/ on purpose. This client's fetch is also used for
// postgrest, storage, and functions, where a large upload may legitimately run
// far longer than any auth call should.

/**
 * One auth request's deadline. Short enough that auth-js's own retry budget
 * (~30s, measured from the first attempt) still fits several attempts inside
 * it, so a genuinely slow network keeps its chance to recover.
 */
export const AUTH_REQUEST_TIMEOUT_MS = 6000;

type FetchLike = (
  input: RequestInfo | URL,
  init?: RequestInit
) => Promise<Response>;

export function isAuthRequest(url: string): boolean {
  return url.includes("/auth/v1/");
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

/**
 * Abort when ANY input signal aborts. `AbortSignal.any` where available, hand
 * rolled otherwise — a missing built-in must not silently drop the deadline,
 * which is the exact failure shape this file exists to remove.
 */
export function anySignal(signals: AbortSignal[]): AbortSignal {
  if (typeof AbortSignal.any === "function") return AbortSignal.any(signals);
  const controller = new AbortController();
  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort(signal.reason);
      break;
    }
    signal.addEventListener("abort", () => controller.abort(signal.reason), {
      once: true,
    });
  }
  return controller.signal;
}

/**
 * Wrap `baseFetch` so auth requests carry a deadline. Other requests pass
 * through untouched. `timeoutMs` is a parameter because `AbortSignal.timeout`
 * runs on a platform-internal clock that test fake-timers cannot advance — the
 * abort has to be provable, not assumed.
 */
export function createBoundedAuthFetch(
  baseFetch: FetchLike,
  timeoutMs: number = AUTH_REQUEST_TIMEOUT_MS
): FetchLike {
  return (input, init) => {
    if (!isAuthRequest(requestUrl(input))) return baseFetch(input, init);
    const deadline = AbortSignal.timeout(timeoutMs);
    // Never clobber a caller's own signal.
    const signal = init?.signal ? anySignal([init.signal, deadline]) : deadline;
    return baseFetch(input, { ...init, signal });
  };
}
